import { createPatientCartMutation } from '@/api/carts/adminPatientCartMutations';
import { getUserCartsQuery } from '@/api/carts/cartQueries';
import { Button } from '@/components/common/Button';
import { ConfirmDeleteDialog } from '@/components/common/ConfirmDeleteDialog';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/common/Table';
import { routes } from '@/routes/routes';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AddNewSubscription } from '../users-common/AddNewSubscription';

interface ModifyUserCartProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: {
    id: number;
    name: string;
    surname: string;
    clinic?: { id: number; name: string };
  } | null;
  initialAdding?: boolean;
}

export function ModifyUserCart({
  open,
  onOpenChange,
  selectedUser,
  initialAdding = false,
}: ModifyUserCartProps) {
  const navigate = useNavigate();
  const [isAdding, setIsAdding] = useState(initialAdding);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cartIdToDelete, setCartIdToDelete] = useState<number | null>(null);

  const {
    data: cartsResponse,
    isLoading,
    refetch,
  } = useQuery({
    ...getUserCartsQuery(selectedUser?.id ?? 0),
    enabled: !!selectedUser?.id && open,
  });

  useEffect(() => {
    if (open && selectedUser?.id) {
      refetch();
    }
  }, [open, selectedUser?.id, refetch]);

  useEffect(() => {
    if (open) {
      setIsAdding(initialAdding);
    }
  }, [open, initialAdding]);

  const { mutate: deleteCart, isPending: isDeleting } = useMutation({
    mutationFn: async (cartId: number) => {
      const { api } = await import('@/lib/api');
      return api.delete(`carts/${cartId}`).json();
    },
    onSuccess: async () => {
      toast.success('Cart deleted successfully');
      await refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete cart');
    },
  });

  const { mutate: createCart, isPending: isCreating } = useMutation({
    ...createPatientCartMutation(),
    onSuccess: (response) => {
      toast.success('Subscription created successfully');
      setIsAdding(false);
      handleEdit(response.id);
    },
  });

  // if (!selectedUser) return null; // Removed early return to allow Dialog to control open state

  const carts = cartsResponse?.data ?? [];

  const handleEdit = (cartId: number) => {
    onOpenChange(false);
    navigate({
      to: routes.dashboard.subscription.cart.items,
      params: { cartId },
    });
  };

  const handleDeleteClick = (cartId: number) => {
    setCartIdToDelete(cartId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (cartIdToDelete !== null) {
      deleteCart(cartIdToDelete);
      setDeleteDialogOpen(false);
      setCartIdToDelete(null);
    }
  };

  const handleAddSubscription = (name: string) => {
    if (selectedUser?.id) {
      createCart({ name, user_id: selectedUser.id });
    }
  };

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title={
        selectedUser
          ? `${selectedUser.name} ${selectedUser.surname}'s Subscriptions`
          : 'Subscriptions'
      }
      maxWidth="4xl"
    >
      <div className="mt-4">
        {!selectedUser ? (
          <p className="text-center text-slate-500">Loading user data...</p>
        ) : isAdding ? (
          <AddNewSubscription
            onSubmit={handleAddSubscription}
            onCancel={
              carts.length === 0 || initialAdding
                ? () => onOpenChange(false)
                : () => setIsAdding(false)
            }
            isPending={isCreating}
          />
        ) : isLoading ? (
          <p className="py-4 text-center text-slate-500">
            Refreshing subscriptions...
          </p>
        ) : carts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <p className="mb-4 text-center text-slate-500">
              No subscriptions found.
            </p>
            <Button
              variant="primary"
              size="md"
              onClick={() => setIsAdding(true)}
            >
              <Plus className="mr-2 size-4" />
              Add New Subscription
            </Button>
          </div>
        ) : (
          <>
            <Table>
              <TableBody>
                {carts.map((cart) => (
                  <TableRow
                    key={cart.id}
                    className="border-none hover:bg-transparent"
                  >
                    <TableCell className="max-w-50 min-w-0 pr-2 break-words whitespace-normal sm:max-w-full">
                      {cart.name}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-4">
                        <RelationActionButton
                          onClick={() => handleEdit(cart.id)}
                        >
                          Edit
                        </RelationActionButton>
                        <RelationActionButton
                          onClick={() => handleDeleteClick(cart.id)}
                          className="text-delete"
                        >
                          Delete
                        </RelationActionButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <ConfirmDeleteDialog
              open={deleteDialogOpen}
              onOpenChange={setDeleteDialogOpen}
              title="Delete subscription"
              description="Are you sure you want to delete this subscription?"
              onConfirm={handleDeleteConfirm}
              isPending={isDeleting}
            />

            <div className="mt-6">
              <Button
                variant="primary"
                size="md"
                onClick={() => setIsAdding(true)}
              >
                <Plus className="mr-2 size-4" />
                Add New Subscription
              </Button>
            </div>
          </>
        )}
      </div>
    </DialogWrapper>
  );
}
